import { memo, useMemo } from 'react';
import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import { CircleRegular, ClockRegular, CheckmarkCircleRegular, DismissCircleRegular, LockClosedRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { usePeriodData } from '../state';

const useStyles = makeStyles({
    badge: {
        display: 'inline-flex',
        alignItems: 'center',
        columnGap: '4px',
        paddingTop: '2px',
        paddingBottom: '2px',
        paddingLeft: '8px',
        paddingRight: '8px',
        borderRadius: tokens.borderRadiusMedium,
        fontSize: '12px',
        fontWeight: tokens.fontWeightSemibold,
        whiteSpace: 'nowrap',
        lineHeight: '20px',
    },
    blue: {
        backgroundColor: tokens.colorBrandBackground2,
        color: tokens.colorBrandForeground2,
    },
    green: {
        backgroundColor: tokens.colorPaletteGreenBackground1,
        color: tokens.colorPaletteGreenForeground1,
    },
    yellow: {
        backgroundColor: tokens.colorPaletteYellowBackground1,
        color: tokens.colorPaletteYellowForeground2,
    },
    grey: {
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground3,
    },
});

export const TimesheetStatusBadge = memo(function TimesheetStatusBadge() {
    const styles = useStyles();
    const { t } = useTranslation('timesheet');
    const periodData = usePeriodData();

    const badge = useMemo(() => {
        if (!periodData) return null;

        const { timesheetState, periodState } = periodData;

        if (periodState === 'Closed') {
            return {
                label: t('statusBadge.closed'),
                colorClass: styles.grey,
                Icon: LockClosedRegular,
            };
        }

        switch (timesheetState) {
            case 'Open': {
                return {
                    label: t('statusBadge.open'),
                    colorClass: styles.blue,
                    Icon: CircleRegular,
                };
            }
            case 'Submitted': {
                return {
                    label: t('statusBadge.awaitingApproval'),
                    colorClass: styles.blue,
                    Icon: ClockRegular,
                };
            }
            case 'Approved': {
                return {
                    label: t('statusBadge.approved'),
                    colorClass: styles.green,
                    Icon: CheckmarkCircleRegular,
                };
            }
            case 'Rejected': {
                return {
                    label: t('statusBadge.rejected'),
                    colorClass: styles.yellow,
                    Icon: DismissCircleRegular,
                };
            }
            default: {
                return null;
            }
        }
    }, [periodData, t, styles]);

    if (!badge) return null;

    const { label, colorClass, Icon } = badge;

    return (
        <span className={mergeClasses(styles.badge, colorClass)}>
            <Icon fontSize={14} />
            {label}
        </span>
    );
});
